const STORAGE_ORIGIN = (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_STORAGE_ORIGIN || "";

export function resolveUrl(url: string | undefined | null): string {
	if (!url) return "";
	if (url.startsWith("data:")) return url;
	if (url.startsWith("/api/storage/file/")) return `${STORAGE_ORIGIN}${url}`;
	return url;
}

export interface MediaFile {
	url?: string;
	_base64?: string;
	filename?: string;
	size?: number;
	mimeType?: string;
	key?: string;
}

export interface FileKeyMap {
	url: string;
	key: string;
	filename: string;
	size: string;
	mimeType: string;
}

export const DEFAULT_FILE_KEYS: FileKeyMap = {
	url: "url",
	key: "key",
	filename: "filename",
	size: "size",
	mimeType: "mimeType",
};

const FILE_KEY_OPTION_TO_LOGICAL: Record<string, keyof FileKeyMap> = {
	keyFileResource: "url",
	keyStorageKey: "key",
	keyFileName: "filename",
	keyFileSize: "size",
	keyFileMimeType: "mimeType",
};

export function resolveFileKeys(
	attr?: { morphedInputTypeOptions?: Array<{ option?: string; value?: unknown }> } | null,
): FileKeyMap {
	const keys: FileKeyMap = { ...DEFAULT_FILE_KEYS };
	const opts = attr?.morphedInputTypeOptions;
	if (Array.isArray(opts)) {
		for (const opt of opts) {
			if (!opt || typeof opt !== "object") continue;
			const logical = FILE_KEY_OPTION_TO_LOGICAL[opt.option ?? ""];
			const val = opt.value;
			if (logical && typeof val === "string" && val.trim()) {
				keys[logical] = val;
			}
		}
	}
	return keys;
}

export function toMediaFile(raw: unknown, keyMap?: FileKeyMap): MediaFile | null {
	if (!raw) return null;
	if (typeof raw === 'string') {
		// morphed stringifica atributos object/file-upload al guardar. Si el valor es
		// un JSON stringificado ('{"url":"..."}'), parsearlo a objeto primero.
		const t = raw.trim();
		if (t.startsWith('{') || t.startsWith('[')) {
			try {
				const parsed = JSON.parse(t);
				if (parsed && typeof parsed === 'object') return toMediaFile(parsed, keyMap);
			} catch { /* no era JSON */ }
		}
		return {url: raw};
	}
	const r = raw as Record<string, unknown>;
	const km = keyMap ?? DEFAULT_FILE_KEYS;
	const mf: MediaFile = {};
	if (r[km.url] != null) mf.url = r[km.url] as string;
	if (r[km.key] != null) mf.key = r[km.key] as string;
	if (r[km.filename] != null) mf.filename = r[km.filename] as string;
	if (r[km.size] != null) mf.size = r[km.size] as number;
	if (r[km.mimeType] != null) mf.mimeType = r[km.mimeType] as string;
	if (r._base64 != null) mf._base64 = r._base64 as string;
	return mf;
}

export function fromMediaFile(mf: MediaFile | null | undefined, keyMap?: FileKeyMap): Record<string, unknown> {
	const km = keyMap ?? DEFAULT_FILE_KEYS;
	const out: Record<string, unknown> = {};
	if (!mf) return out;
	if (mf.url != null) out[km.url] = mf.url;
	if (mf.key != null) out[km.key] = mf.key;
	if (mf.filename != null) out[km.filename] = mf.filename;
	if (mf.size != null) out[km.size] = mf.size;
	if (mf.mimeType != null) out[km.mimeType] = mf.mimeType;
	if (mf._base64 != null) out._base64 = mf._base64;
	return out;
}

export function normalizeFile(raw: unknown): MediaFile {
	if (!raw) return {};
	if (typeof raw === "string") return { url: raw };
	return raw as MediaFile;
}

export function fileUrl(raw: unknown): string {
	const f = normalizeFile(raw);
	return f._base64 ?? resolveUrl(f.url);
}

export function fileKind(raw: unknown, accept?: string): "image" | "video" | "file" {
	const f = normalizeFile(raw);
	const mt = f.mimeType || "";
	if (mt.startsWith("image/")) return "image";
	if (mt.startsWith("video/")) return "video";
	if (accept?.startsWith("image/") && !mt) return "image";
	if (accept?.startsWith("video/") && !mt) return "video";
	return "file";
}

export function formatSize(bytes?: number): string {
	if (!bytes) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function getInitials(name: string): string {
	return name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

// === Redimensionado/compresión client-side de imágenes ===
// Evita payloads enormes (base64 de high-res) que provocan 413 en App Engine.
// Configuración por defecto; puede sobreescribirse vía propiedades del componente.
export const DEFAULT_IMAGE_MAX_DIM = 1600;
export const DEFAULT_IMAGE_MIN_DIM = 64;
export const DEFAULT_IMAGE_QUALITY = 0.82;
export const DEFAULT_IMAGE_MAX_BYTES = 6 * 1024 * 1024; // ~6MB tras resize

function readFileAsDataURL(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = src;
	});
}

/**
 * Recorre un valor y, donde haya strings que sean JSON stringificado
 * ('{"url":...}', '["a","b"]'), los parsea a objeto/array. Rompe el ciclo de
 * stringify progresivo de morphed (object/file-upload attrs) al cargar registros.
 * Strings legítimos (URLs, paths) no son JSON válido → quedan igual.
 */
export function deepParseJsonStrings(v: unknown): unknown {
	if (typeof v === 'string') {
		const t = v.trim();
		// JSON stringificado por morphed: '"https://..."', '{}', 'null', '{"url":...}', '[...]'.
		// Los strings legítimos (URLs, paths, texto) NO arrancan con comilla/llave/corchete.
		if ((t.startsWith('"') && t.endsWith('"')) ||
			(t.startsWith('{') && t.endsWith('}')) ||
			(t.startsWith('[') && t.endsWith(']'))) {
			try {
				return deepParseJsonStrings(JSON.parse(t));
			} catch { /* no era JSON */ }
		}
		return v;
	}
	if (Array.isArray(v)) return v.map(deepParseJsonStrings);
	if (v && typeof v === 'object') {
		const out: Record<string, unknown> = {};
		for (const [k, val] of Object.entries(v)) out[k] = deepParseJsonStrings(val);
		return out;
	}
	return v;
}

export interface ResizeResult {
	dataUrl: string;
	width: number;
	height: number;
	resized: boolean;
}

/**
 * Convierte un File a data-URL base64. Si es imagen y supera `maxDim`, la
 * redimensiona (downscale manteniendo aspecto) y la recomprime (JPEG/PNG).
 * No-imágenes (PDF, zip, etc.) se devuelven tal cual (raw base64).
 */
export async function fileToResizedDataUrl(
	file: File,
	maxDim = DEFAULT_IMAGE_MAX_DIM,
	quality = DEFAULT_IMAGE_QUALITY,
): Promise<ResizeResult> {
	if (!file.type.startsWith("image/")) {
		const dataUrl = await readFileAsDataURL(file);
		return {dataUrl, width: 0, height: 0, resized: false};
	}
	const original = await readFileAsDataURL(file);
	const img = await loadImageEl(original);
	let {width, height} = img;
	if (width <= 0 || height <= 0) return {dataUrl: original, width, height, resized: false};
	const scale = Math.min(maxDim / width, maxDim / height, 1);
	if (scale >= 1) return {dataUrl: original, width, height, resized: false};
	width = Math.round(width * scale);
	height = Math.round(height * scale);
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return {dataUrl: original, width: img.width, height: img.height, resized: false};
	ctx.drawImage(img, 0, 0, width, height);
	// PNG mantiene transparencia; el resto a JPEG (mejor compresión).
	const outType = file.type === "image/png" ? "image/png" : "image/jpeg";
	const dataUrl = canvas.toDataURL(outType, quality);
	return {dataUrl, width, height, resized: true};
}
